import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, ArrowRight, Building, BarChart3, Settings, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export default function Welcome() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
      return;
    }
  }, [user, loading, navigate]);

  const steps = [
    {
      title: 'Bienvenue !',
      description: `Votre compte Demo est maintenant actif`,
      icon: CheckCircle,
      color: 'text-green-600'
    },
    {
      title: 'Votre tableau de bord',
      description: 'Accédez à vos audits et analyses Amazon personnalisés',
      icon: BarChart3,
      color: 'text-blue-600'
    },
    {
      title: 'Support dédié',
      description: 'Notre équipe est disponible pour vous accompagner',
      icon: Users,
      color: 'text-purple-600'
    }
  ];

  const nextStep = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      navigate('/dashboard');
    }
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const currentStep = steps[step];
  const Icon = currentStep.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/10 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl space-y-8">
        {/* Progress indicator */}
        <div className="flex items-center justify-center space-x-2">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`h-2 w-8 rounded-full transition-colors ${
                index <= step ? 'bg-primary' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Welcome card */}
        <Card className="text-center">
          <CardHeader className="space-y-6">
            <div className="mx-auto">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-50 ${currentStep.color}`}>
                <Icon className="h-8 w-8" />
              </div>
            </div>
            
            <div className="space-y-2">
              <CardTitle className="text-2xl font-bold">{currentStep.title}</CardTitle>
              <CardDescription className="text-lg">
                {currentStep.description}
              </CardDescription>
            </div>

            {step === 0 && (
              <div className="flex items-center justify-center">
                <Badge variant="outline" className="px-4 py-2 text-base">
                  <Building className="h-4 w-4 mr-2" />
                  Demo Client
                </Badge>
              </div>
            )}
          </CardHeader>

          <CardContent className="space-y-6">
            {step === 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
                <div className="p-4 border rounded-lg">
                  <BarChart3 className="h-6 w-6 text-blue-600 mb-2" />
                  <h3 className="font-medium">Audits personnalisés</h3>
                  <p className="text-sm text-muted-foreground">Analyses complètes de votre présence Amazon</p>
                </div>
                <div className="p-4 border rounded-lg">
                  <Settings className="h-6 w-6 text-green-600 mb-2" />
                  <h3 className="font-medium">Optimisations</h3>
                  <p className="text-sm text-muted-foreground">Recommandations actionables</p>
                </div>
                <div className="p-4 border rounded-lg">
                  <Users className="h-6 w-6 text-purple-600 mb-2" />
                  <h3 className="font-medium">Support expert</h3>
                  <p className="text-sm text-muted-foreground">Accompagnement personnalisé</p>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4 text-left">
                <h3 className="font-medium">Vos prochaines étapes :</h3>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-3 flex-shrink-0" />
                    <span>Lancez votre premier audit stratégique</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-3 flex-shrink-0" />
                    <span>Analysez votre présence Amazon</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-3 flex-shrink-0" />
                    <span>Découvrez vos stratégies de bundle</span>
                  </li>
                </ul>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4 text-left">
                <h3 className="font-medium">Nous sommes là pour vous :</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="font-medium text-blue-900">📧 Email</p>
                    <p className="text-sm text-blue-700">hello@cosmos-wave.com</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="font-medium text-green-900">💬 WhatsApp</p>
                    <p className="text-sm text-green-700">Support instantané</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {step < steps.length - 1 ? (
                <>
                  <Button variant="outline" onClick={goToDashboard}>
                    Passer l'introduction
                  </Button>
                  <Button onClick={nextStep}>
                    Suivant
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </>
              ) : (
                <Button onClick={goToDashboard} size="lg">
                  Accéder au tableau de bord
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick links */}
        <div className="text-center text-sm text-muted-foreground">
          Vous pouvez accéder à ce guide à tout moment depuis votre tableau de bord
        </div>
      </div>
    </div>
  );
}